/* ==========================================================================
   XTRUDE — xtrude.js
   Hand-authored vanilla JS. Native web components only where interactivity
   is genuinely needed: drawers, carousels, AJAX cart, variant picker.
   ========================================================================== */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------------------------
     Drawer (mobile nav / cart / filters)
     Bottom sheet or side panel is decided purely in CSS; this class only
     manages open state, focus trapping and scroll locking.
     ------------------------------------------------------------------------ */
  class XDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target === this) this.close(); // overlay click
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => this.onKeydown(e));
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.setAttribute('open', '');
      document.body.classList.add('scroll-lock');
      const first = this.querySelector(FOCUSABLE);
      (first || this).focus();
      document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id: this.id } }));
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      if (!document.querySelector('x-drawer[open]')) {
        document.body.classList.remove('scroll-lock');
      }
      if (this.trigger && this.trigger.isConnected) this.trigger.focus();
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = [...this.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('x-drawer', XDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer) {
      e.preventDefault();
      drawer.open(opener);
    }
  });

  /* ------------------------------------------------------------------------
     Carousel — progressive enhancement over a native scroll-snap track.
     Swipe always works without JS; this adds arrows, dots and index state.
     ------------------------------------------------------------------------ */
  class XCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.index = 0;

      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.dotsWrap = this.querySelector('[data-dots]');

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      if (this.dotsWrap) {
        this.slides.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel-dot';
          dot.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
          dot.addEventListener('click', () => this.goTo(i));
          this.dotsWrap.append(dot);
        });
        this.dots = [...this.dotsWrap.children];
      }

      let raf = null;
      this.track.addEventListener(
        'scroll',
        () => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = null;
            this.syncIndex();
          });
        },
        { passive: true }
      );
      this.syncIndex();
      this.initAutoplay();
    }

    initAutoplay() {
      const seconds = parseFloat(this.dataset.autoplay || '0');
      if (!(seconds > 0) || reducedMotion.matches || this.slides.length < 2) return;

      const advance = () => this.goTo((this.index + 1) % this.slides.length);
      const start = () => {
        this.stopAutoplay();
        this.autoplayTimer = setInterval(advance, seconds * 1000);
      };
      this.stopAutoplay = () => clearInterval(this.autoplayTimer);

      start();
      this.addEventListener('pointerenter', () => this.stopAutoplay());
      this.addEventListener('pointerleave', start);
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else start();
      });
    }

    syncIndex() {
      const mid = this.track.scrollLeft + this.track.clientWidth / 2;
      let best = -1;
      this.slides.forEach((slide, i) => {
        if (slide.hidden) return;
        const center = slide.offsetLeft + slide.offsetWidth / 2;
        if (best === -1 || Math.abs(center - mid) < Math.abs(this.slides[best].offsetLeft + this.slides[best].offsetWidth / 2 - mid)) {
          best = i;
        }
      });
      if (best === -1) best = 0;
      if (best !== this.index) {
        this.index = best;
        this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index: best } }));
      }
      this.updateUi();
    }

    updateUi() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      const firstStart = this.slides.length ? this.slideStart(this.slides[0]) : 0;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= firstStart + 2;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= maxScroll;
      if (this.dots) {
        this.dots.forEach((d, i) => d.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
      }
    }

    // A slide's snap-rest scrollLeft: mandatory snap aligns the slide's left
    // edge to the track's border edge (track padding scrolls away with the
    // content), so the rest position is simply the rect delta plus current
    // scroll. Rect-based, so it's immune to offsetParent quirks.
    slideStart(slide) {
      return (
        slide.getBoundingClientRect().left -
        this.track.getBoundingClientRect().left +
        this.track.scrollLeft
      );
    }

    // Step relative to the actual scroll position, not the centered index —
    // with snap-align:start the leftmost slide is already at the edge, so an
    // index-based "previous" would target it and scroll zero pixels.
    step(dir) {
      const cur = this.track.scrollLeft;
      let target = -1;
      if (dir > 0) {
        target = this.slides.findIndex((s) => this.slideStart(s) > cur + 2);
        if (target === -1) target = this.slides.length - 1;
      } else {
        for (let k = 0; k < this.slides.length; k += 1) {
          if (this.slideStart(this.slides[k]) < cur - 2) target = k;
        }
        if (target === -1) target = 0;
      }
      this.goTo(target);
    }

    goTo(i) {
      const target = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
      if (!target) return;
      this.track.scrollTo({
        left: this.slideStart(target),
        behavior: scrollBehavior(),
      });
      // iOS can strand an interrupted smooth scroll between snap points (a
      // sliver of the neighbour slide stays visible). Re-snap only when the
      // rest position matches no slide at all, so a user mid-swipe is never
      // fought.
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(() => {
        const sl = this.track.scrollLeft;
        const stranded = !this.slides.some((s) => Math.abs(this.slideStart(s) - sl) <= 2);
        if (stranded) {
          // Correct INSTANTLY (smooth scrollTo is what strands on WebKit) —
          // override the track's CSS scroll-behavior for one assignment.
          const prev = this.track.style.scrollBehavior;
          this.track.style.scrollBehavior = 'auto';
          this.track.scrollLeft = this.slideStart(this.slides[this.index]);
          this.track.style.scrollBehavior = prev;
        }
      }, 900);
    }
  }
  customElements.define('x-carousel', XCarousel);

  /* ------------------------------------------------------------------------
     GA4 ecommerce — shared item mapper. Shopify's cart/add.js response and
     Liquid's cart.items use the same field names (product_title,
     variant_title, sku, price in cents, quantity), so one mapper covers both.
     ------------------------------------------------------------------------ */
  const GA4 = {
    item(it) {
      return {
        item_id: it.sku || String(it.variant_id || it.id || ''),
        item_name: it.product_title,
        item_variant: it.variant_title || undefined,
        price: (it.price || 0) / 100,
        quantity: it.quantity || 1,
      };
    },
    event(name, rawItems, extra) {
      if (typeof gtag !== 'function' || !rawItems.length) return;
      const items = rawItems.map(GA4.item);
      const value = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
      gtag('event', name, Object.assign({ currency: 'AED', value: Math.round(value * 100) / 100, items }, extra || {}));
    },
  };

  /* ------------------------------------------------------------------------
     Cart — AJAX add + drawer re-render via the Section Rendering API.
     ------------------------------------------------------------------------ */
  const Cart = {
    async refreshDrawer(openAfter) {
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const data = await res.json();
      const doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      const fresh = doc.querySelector('[data-drawer-inner]');
      const current = document.querySelector('#cart-drawer [data-drawer-inner]');
      if (fresh && current) {
        current.replaceWith(fresh);
        const count = fresh.dataset.cartItemCount || '0';
        document.querySelectorAll('[data-cart-count]').forEach((el) => (el.textContent = count));
      }
      if (openAfter) {
        document.getElementById('cart-drawer')?.open();
        // view_cart — the drawer is genuinely being shown to the user now.
        const itemsEl = document.querySelector('#cart-drawer [data-cart-items]');
        if (itemsEl) {
          try { GA4.event('view_cart', JSON.parse(itemsEl.textContent)); } catch (e) {}
        }
      }
    },

    async change(key, quantity) {
      const drawer = document.querySelector('#cart-drawer [data-drawer-inner]');
      if (drawer) drawer.setAttribute('aria-busy', 'true');
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      await this.refreshDrawer(false);
    },
  };

  // Pre-order confirmation modal — resolves true (continue) / false (cancel).
  const PreorderModal = {
    confirm() {
      const modal = document.querySelector('[data-preorder-modal]');
      if (!modal) return Promise.resolve(true); // no modal on page → just proceed
      return new Promise((resolve) => {
        const close = (result) => {
          modal.hidden = true;
          document.body.classList.remove('scroll-lock');
          modal.removeEventListener('click', onClick);
          document.removeEventListener('keydown', onKey);
          resolve(result);
        };
        const onClick = (e) => {
          if (e.target.closest('[data-preorder-confirm]')) close(true);
          else if (e.target.closest('[data-preorder-cancel]')) close(false);
        };
        const onKey = (e) => {
          if (e.key === 'Escape') close(false);
        };
        modal.hidden = false;
        document.body.classList.add('scroll-lock');
        modal.addEventListener('click', onClick);
        document.addEventListener('keydown', onKey);
        modal.querySelector('[data-preorder-confirm]')?.focus();
      });
    },
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-add]');
    if (!form) return;
    e.preventDefault();

    // If this add is a pre-order item, confirm via the 15-day popup first.
    const isPreorder =
      e.submitter?.dataset.preorder === 'true' ||
      !!document.querySelector('[data-atc][data-preorder="true"]');
    if (isPreorder) {
      const go = await PreorderModal.confirm();
      if (!go) return;
    }

    const buttons = document.querySelectorAll('[data-atc]');
    const errorEl = form.querySelector('[data-form-error]');
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    if (errorEl) errorEl.hidden = true;

    try {
      let res = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      // Pre-order items oversell by design (inventory_policy: continue), so a
      // "sold out" rejection can only be a transient platform/sync blip —
      // retry once quietly before surfacing anything to the customer.
      if (!res.ok && isPreorder) {
        await new Promise((r) => setTimeout(r, 900));
        res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
      }
      if (!res.ok) {
        const err = await res.json();
        // Shopify's own message (sold out, quantity limits, etc.) — safe and
        // helpful to show verbatim, EXCEPT for pre-order items, where raw
        // "already sold out" text contradicts the Pre Order button.
        let msg = err.description || err.message || 'Error';
        if (isPreorder && /sold out/i.test(msg)) {
          msg = window.xtrudeStrings?.preorderError || 'We could not reserve your pre-order just now. Please refresh the page and try again.';
        }
        const cartError = new Error(msg);
        cartError.isCartError = true;
        throw cartError;
      }
      const added = await res.json();
      // Analytics must never be able to break the purchase flow — isolate it.
      try { GA4.event('add_to_cart', [added]); } catch (gaErr) { console.error('GA4 add_to_cart failed:', gaErr); }
      await Cart.refreshDrawer(true);
    } catch (err) {
      if (errorEl) {
        // Anything that ISN'T a known Shopify cart error (a network hiccup, a
        // browser quirk, an unexpected exception) shows a friendly fallback
        // instead of raw technical text — the real error still goes to console.
        if (err.isCartError) {
          errorEl.textContent = err.message;
        } else {
          console.error('Add to cart failed:', err);
          errorEl.textContent = window.xtrudeStrings?.addToCartError || 'Something went wrong adding this to your cart. Please try again.';
        }
        errorEl.hidden = false;
      }
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  });

  // GA4 ecommerce: begin_checkout — the real Shopify checkout hand-off, only
  // on the full cart page's submit button (the drawer's "Checkout" link just
  // routes to /cart, no navigation-away race there).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('form[data-cart-page] button[name="checkout"]');
    if (!btn) return;
    const itemsEl = document.querySelector('[data-cart-page-items]');
    if (!itemsEl) return;
    try {
      const rawItems = JSON.parse(itemsEl.textContent);
      GA4.event('begin_checkout', rawItems, { transport_type: 'beacon' });
    } catch (err) {}
  });

  // Cart drawer line controls (event delegation survives re-renders)
  document.addEventListener('click', (e) => {
    const stepBtn = e.target.closest('[data-qty-change]');
    if (stepBtn) {
      const line = stepBtn.closest('[data-line]');
      const input = line.querySelector('[data-qty-input]');
      const next = Math.max(0, parseInt(input.value || '0', 10) + parseInt(stepBtn.dataset.qtyChange, 10));
      Cart.change(line.dataset.line, next);
      return;
    }
    const removeBtn = e.target.closest('[data-line-remove]');
    if (removeBtn) Cart.change(removeBtn.dataset.lineRemove, 0);
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('#cart-drawer [data-qty-input]');
    if (!input) return;
    const line = input.closest('[data-line]');
    Cart.change(line.dataset.line, Math.max(0, parseInt(input.value || '0', 10)));
  });

  /* ------------------------------------------------------------------------
     Product page — variant picker
     ------------------------------------------------------------------------ */
  class XVariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      // Variant IDs in a pre-order state (emitted server-side; the storefront
      // variant JSON doesn't carry inventory_quantity).
      const preorderJson = this.querySelector('[data-preorder-ids]');
      this.preorderIds = preorderJson ? JSON.parse(preorderJson.textContent) : [];
      this.form = document.getElementById(this.dataset.formId);
      this.colorFieldset = this.querySelector('fieldset[data-option-name="color"], fieldset[data-option-name="colour"]');
      this.lastColor = null;
      this.addEventListener('change', () => this.onChange());
      this.filterGallery();
    }

    // Show only the selected color's images (matched via data-color from media alt).
    // Returns true when it owns the gallery view, so onChange skips the media jump.
    filterGallery() {
      const gallery = document.querySelector('[data-gallery]');
      if (!gallery || !this.colorFieldset) return false;
      const selected = (this.colorFieldset.querySelector('input:checked')?.value || '').toLowerCase();
      const slides = gallery.slides || [...gallery.querySelectorAll('.carousel__slide')];
      if (!slides.some((s) => s.dataset.color === selected)) return false;
      if (selected === this.lastColor) return true;
      this.lastColor = selected;

      const thumbs = [...document.querySelectorAll('[data-thumbs] [data-thumb]')];
      slides.forEach((slide, i) => {
        const show = !slide.dataset.color || slide.dataset.color === selected;
        slide.hidden = !show;
        if (thumbs[i]) thumbs[i].hidden = !show;
        if (gallery.dots && gallery.dots[i]) gallery.dots[i].hidden = !show;
      });
      const first = slides.findIndex((s) => !s.hidden);
      if (first > -1 && typeof gallery.goTo === 'function') gallery.goTo(first);
      if (typeof gallery.updateUi === 'function') gallery.updateUi();
      return true;
    }

    selectedOptions() {
      const fieldsets = [...this.querySelectorAll('fieldset')];
      return fieldsets.map(
        (fs) => fs.querySelector('input:checked')?.value ?? fs.querySelector('select')?.value
      );
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === options[i]));

      options.forEach((value, i) => {
        const label = this.querySelector(`[data-option-label="${i}"]`);
        if (label && value) label.textContent = value;
      });

      const atcButtons = document.querySelectorAll('[data-atc]');
      const atcLabels = document.querySelectorAll('[data-atc-label]');
      const strings = window.xtrudeStrings || {};

      if (!variant) {
        atcButtons.forEach((b) => (b.disabled = true));
        atcLabels.forEach((l) => (l.textContent = strings.unavailable || 'Unavailable'));
        return;
      }

      const idInput = this.form?.querySelector('[data-variant-id]');
      if (idInput) idInput.value = variant.id;

      document.querySelectorAll('[data-price]').forEach((el) => {
        el.textContent = this.formatMoney(variant.price);
      });
      document.querySelectorAll('[data-compare-price]').forEach((el) => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          el.textContent = this.formatMoney(variant.compare_at_price);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });

      // Pre-order: purchasable (oversell allowed) but tracked stock is depleted.
      // Membership list is computed server-side (see [data-preorder-ids]).
      const isPreorder = variant.available && this.preorderIds.includes(variant.id);

      atcButtons.forEach((b) => {
        b.disabled = !variant.available;
        b.dataset.preorder = isPreorder ? 'true' : 'false';
      });

      // Pre-order marker inputs (line item properties) + the visible delivery
      // note follow the selected variant.
      this.form?.querySelectorAll('[data-preorder-prop]').forEach((i) => (i.disabled = !isPreorder));
      document.querySelectorAll('[data-preorder-note]').forEach((n) => (n.hidden = !isPreorder));
      atcLabels.forEach((l) => {
        if (!variant.available) {
          l.textContent = strings.soldOut || 'Sold out';
        } else if (isPreorder) {
          l.textContent = strings.preorder || 'Pre Order';
        } else {
          l.textContent = strings.addToCart || 'Add to cart';
        }
      });

      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Color filter owns the gallery when media are color-tagged;
      // otherwise fall back to jumping to the variant's image
      if (!this.filterGallery() && variant.featured_media) {
        const gallery = document.querySelector('[data-gallery]');
        const slide = gallery?.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (gallery && slide) {
          const slides = [...gallery.querySelectorAll('.carousel__slide')];
          gallery.goTo(slides.indexOf(slide));
        }
      }
    }

    formatMoney(cents) {
      const format = window.xtrudeMoneyFormat || '{{ amount }}';
      const amount = (cents / 100).toFixed(2);
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }
  }
  customElements.define('x-variant-picker', XVariantPicker);

  // Quantity steppers on the product form
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const input = btn.closest('.qty').querySelector('.qty__input');
    const min = parseInt(input.min || '1', 10);
    input.value = Math.max(min, parseInt(input.value || '1', 10) + parseInt(btn.dataset.qtyStep, 10));
  });

  // Discount code field — applies the code to the cart itself through the
  // Ajax API (/cart/update.js { discount }), so the discount line and the new
  // total show right here in the cart/drawer. Shopify's /discount cookie is
  // still primed as a belt-and-braces so checkout receives the code too.
  const DISCOUNT_KEY = 'xtrude-discount';
  const DISCOUNT_MODE_KEY = 'xtrude-discount-mode'; // 'cart' | 'checkout'
  const discountStrings = () => window.xtrudeStrings || {};
  const withCode = (s, code) => (s || '').replace('__CODE__', (code || '').toUpperCase());
  const postCart = (payload) =>
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then((r) => r.json());

  const setDiscountMsg = (form, text, state, code) => {
    const msg = form.querySelector('[data-discount-msg]');
    if (!msg) return;
    msg.textContent = text;
    msg.classList.toggle('is-ok', state === 'ok');
    msg.classList.toggle('is-error', state === 'error');
    if (state === 'ok' && code) {
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'discount-field__remove';
      rm.dataset.discountRemove = code;
      rm.textContent = discountStrings().discountRemove || 'Remove';
      msg.append(' ', rm);
    }
    msg.hidden = false;
  };

  const clearDiscountFields = () => {
    document.querySelectorAll('[data-discount-form]').forEach((f) => {
      const input = f.querySelector('.discount-field__input');
      if (input) input.value = '';
      const msg = f.querySelector('[data-discount-msg]');
      if (msg) { msg.hidden = true; msg.textContent = ''; }
    });
  };

  // Show the saved code (and state) in every discount field.
  const hydrateDiscountFields = () => {
    const saved = sessionStorage.getItem(DISCOUNT_KEY);
    if (!saved) return;
    const inCart = sessionStorage.getItem(DISCOUNT_MODE_KEY) === 'cart';
    const s = discountStrings();
    document.querySelectorAll('[data-discount-form]').forEach((form) => {
      const input = form.querySelector('.discount-field__input');
      if (input && !input.value) input.value = saved;
      setDiscountMsg(form, withCode(inCart ? s.discountAppliedCart : s.discountApplied, saved), 'ok', saved);
    });
  };

  // Re-render the cart page totals and the drawer after the cart changed.
  const refreshCartViews = async () => {
    const totals = document.querySelector('[data-cart-totals]');
    if (totals) {
      try {
        const res = await fetch(`${window.location.pathname}?sections=main-cart`, { credentials: 'same-origin' });
        const data = await res.json();
        const doc = new DOMParser().parseFromString(data['main-cart'], 'text/html');
        const fresh = doc.querySelector('[data-cart-totals]');
        if (fresh) totals.replaceWith(fresh);
      } catch (err) { /* totals are right again on the next full load */ }
    }
    if (document.querySelector('#cart-drawer [data-drawer-inner]')) {
      try { await Cart.refreshDrawer(false); } catch (err) { /* drawer keeps its last render */ }
      hydrateDiscountFields();
    }
  };

  const applyDiscount = async (form, code) => {
    const s = discountStrings();
    const btn = form.querySelector('[data-discount-apply]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    try {
      const cart = await postCart({ discount: code });
      const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : null;
      if (codes) {
        const entry = codes.find((c) => (c.code || '').toUpperCase() === code.toUpperCase());
        if (!entry || !entry.applicable) {
          // Shopify replaces the cart's code set on every update, so a bad
          // attempt also dropped any code that was already applied — put
          // that one back (or clear) and re-render so the page stays truthful.
          const previous = sessionStorage.getItem(DISCOUNT_KEY);
          const hadValid = previous && sessionStorage.getItem(DISCOUNT_MODE_KEY) === 'cart' && previous.toUpperCase() !== code.toUpperCase();
          await postCart({ discount: hadValid ? previous : '' });
          if (!hadValid) {
            sessionStorage.removeItem(DISCOUNT_KEY);
            sessionStorage.removeItem(DISCOUNT_MODE_KEY);
          }
          await refreshCartViews(); // re-hydrates fields with the kept code…
          setDiscountMsg(form, withCode(s.discountInvalid || '__CODE__ is not valid for this order.', code), 'error'); // …then the error wins here
          return;
        }
        sessionStorage.setItem(DISCOUNT_KEY, code);
        sessionStorage.setItem(DISCOUNT_MODE_KEY, 'cart');
        fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {});
        setDiscountMsg(form, withCode(s.discountAppliedCart || 'Code __CODE__ applied — your total is updated.', code), 'ok', code);
        await refreshCartViews();
        return;
      }
      // Storefront without cart-level discount support: prime checkout only.
      sessionStorage.setItem(DISCOUNT_KEY, code);
      sessionStorage.setItem(DISCOUNT_MODE_KEY, 'checkout');
      await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
      setDiscountMsg(form, withCode(s.discountApplied, code), 'ok', code);
    } catch (err) {
      setDiscountMsg(form, s.discountError || 'Could not apply the code — try again.', 'error');
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-discount-apply]');
    if (!btn) return;
    const form = btn.closest('[data-discount-form]');
    if (!form) return;
    const input = form.querySelector('.discount-field__input');
    const code = (input.value || '').trim();
    if (!code) {
      setDiscountMsg(form, discountStrings().discountEmpty || 'Type a code first.', 'error');
      return;
    }
    applyDiscount(form, code);
  });

  // Enter in the code field applies it (the field lives inside the cart form,
  // so a bare Enter would otherwise submit the whole cart).
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || !e.target.classList?.contains('discount-field__input')) return;
    e.preventDefault();
    e.target.closest('[data-discount-form]')?.querySelector('[data-discount-apply]')?.click();
  });

  document.addEventListener('click', async (e) => {
    const rm = e.target.closest('[data-discount-remove]');
    if (!rm) return;
    const form = rm.closest('[data-discount-form]');
    try { await postCart({ discount: '' }); } catch (err) { /* cleared on next load */ }
    sessionStorage.removeItem(DISCOUNT_KEY);
    sessionStorage.removeItem(DISCOUNT_MODE_KEY);
    clearDiscountFields();
    if (form) setDiscountMsg(form, discountStrings().discountRemoved || 'Code removed.', 'neutral');
    await refreshCartViews();
  });

  hydrateDiscountFields();

  // Cart page self-heal: a saved code that isn't reflected in the rendered
  // totals (cart rebuilt, session restored) gets re-applied silently.
  (async () => {
    const saved = sessionStorage.getItem(DISCOUNT_KEY);
    const totals = document.querySelector('[data-cart-totals]');
    if (!saved || !totals || totals.querySelector('.cart-page__row--discount')) return;
    if (sessionStorage.getItem(DISCOUNT_MODE_KEY) !== 'cart') return;
    try {
      const cart = await postCart({ discount: saved });
      const ok = (cart.discount_codes || []).some((c) => c.applicable && (c.code || '').toUpperCase() === saved.toUpperCase());
      if (ok) await refreshCartViews();
    } catch (err) { /* leave as rendered */ }
  })();

  // Checkout with a saved code: sync the cart form first (quantities, note),
  // then go to checkout through /discount so the code arrives pre-applied.
  document.addEventListener('click', async (e) => {
    const checkoutBtn = e.target.closest('[name="checkout"]');
    if (!checkoutBtn) return;
    const code = sessionStorage.getItem(DISCOUNT_KEY);
    if (!code) return;
    e.preventDefault();
    const form = checkoutBtn.closest('form');
    if (form) {
      try {
        await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
      } catch (err) { /* checkout still proceeds with the last saved cart */ }
    }
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/checkout`;
  });

  // Announcement bar — click (or Enter/Space) anywhere on the marquee to copy
  // the code, with a "Copied!" flash. Falls back if clipboard API is blocked.
  const copyAnnounce = async (bar) => {
    const code = bar.dataset.announceCopy;
    if (!code || bar.classList.contains('is-copied')) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const t = document.createElement('textarea');
        t.value = code; t.style.position = 'fixed'; t.style.opacity = '0';
        document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove();
      }
      bar.classList.add('is-copied');
      setTimeout(() => bar.classList.remove('is-copied'), 1600);
    } catch (err) { /* clipboard blocked — no-op */ }
  };
  document.addEventListener('click', (e) => {
    const bar = e.target.closest('[data-announce-copy]');
    if (bar) copyAnnounce(bar);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const bar = e.target.closest('[data-announce-copy]');
    if (bar) { e.preventDefault(); copyAnnounce(bar); }
  });

  // "Size chart" link — opens the size-guide accordion and scrolls to it
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-size-chart]')) return;
    const guide = document.querySelector('[data-size-guide]');
    if (!guide) return;
    guide.open = true;
    guide.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
  });

  /* ------------------------------------------------------------------------
     Product gallery — thumbnail rail sync
     ------------------------------------------------------------------------ */
  const gallery = document.querySelector('[data-gallery]');
  const thumbs = document.querySelector('[data-thumbs]');
  if (gallery && thumbs) {
    thumbs.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-thumb]');
      if (thumb) gallery.goTo(parseInt(thumb.dataset.thumb, 10));
    });
    gallery.addEventListener('carousel:change', (e) => {
      thumbs.querySelectorAll('[data-thumb]').forEach((t, i) => {
        t.setAttribute('aria-current', i === e.detail.index ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------------------------------
     Sticky add-to-cart bar — shows once the buy box scrolls out of view
     ------------------------------------------------------------------------ */
  const buyBox = document.querySelector('[data-buy-box]');
  const stickyBar = document.querySelector('[data-sticky-atc]');
  if (buyBox && stickyBar && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        stickyBar.classList.toggle('is-visible', show);
        stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
      },
      { threshold: 0 }
    ).observe(buyBox);
  }

  /* ------------------------------------------------------------------------
     Misc: auto-submit selects, desktop footer groups, header dropdowns
     ------------------------------------------------------------------------ */
  document.addEventListener('change', (e) => {
    const el = e.target.closest('[data-auto-submit]');
    if (el) el.closest('form').submit();
  });

  // Scroll-reveal: elements with [data-reveal] fade-rise into view once
  const revealEls = document.querySelectorAll('[data-reveal]');
  if (revealEls.length && 'IntersectionObserver' in window && !reducedMotion.matches) {
    const revealer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          obs.unobserve(entry.target);
          // once settled, drop the animating classes entirely so the element
          // returns to plain static paint — a transformed/faded GPU layer can
          // render a slightly different tone than the page on some displays
          setTimeout(() => {
            entry.target.classList.remove('will-reveal', 'is-in');
            entry.target.style.removeProperty('--rd');
          }, 1500);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    revealEls.forEach((el) => {
      el.classList.add('will-reveal');
      revealer.observe(el);
    });
  }

  // PDP section nav — scrollspy highlights the section in view
  const pdpNav = document.querySelector('[data-pdp-nav]');
  if (pdpNav && 'IntersectionObserver' in window) {
    const links = [...pdpNav.querySelectorAll('.pdp-nav__link')];
    const linkFor = new Map();
    links.forEach((link) => {
      const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (target) linkFor.set(target, link);
    });
    const spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const active = linkFor.get(entry.target);
          links.forEach((l) => l.classList.toggle('is-active', l === active));
        });
      },
      { rootMargin: '-25% 0px -65% 0px' }
    );
    linkFor.forEach((_, target) => spy.observe(target));
  }

  // Giant footer wordmark rises out of the divider line when scrolled into view
  const footerDisplay = document.querySelector('[data-footer-display]');
  if (
    footerDisplay &&
    'IntersectionObserver' in window &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    footerDisplay.classList.add('will-rise');
    new IntersectionObserver(
      (entries, obs) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        footerDisplay.classList.add('is-risen');
        obs.disconnect();
      },
      { threshold: 0.3 }
    ).observe(footerDisplay);
  }

  const desktop = window.matchMedia('(min-width: 1024px)');
  const syncFooterGroups = () => {
    document.querySelectorAll('[data-footer-group]').forEach((d) => {
      d.open = desktop.matches;
      d.classList.toggle('is-static', desktop.matches);
    });
  };
  syncFooterGroups();
  desktop.addEventListener('change', syncFooterGroups);

  // Close header dropdowns on outside click / Escape
  document.addEventListener('click', (e) => {
    document.querySelectorAll('[data-dropdown][open]').forEach((d) => {
      if (!d.contains(e.target)) d.open = false;
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-dropdown][open]').forEach((d) => {
      d.open = false;
      d.querySelector('summary')?.focus();
    });
  });

  // Price display conversion lives in currency.js (CurrencyManager) —
  // it watches the DOM, so variant/cart price writes here get converted.
})();

/* PDP sticky buy bar — reveals once the main buy box has scrolled past, so the
   price and CTA stay reachable on a long landing page. Replaces the sticky
   add-to-cart that lived in the (now removed) section nav. */
(function () {
  var bar = document.querySelector('[data-pdp-sticky]');
  var buyBox = document.querySelector('.pdp__buy');
  if (!bar || !buyBox || !('IntersectionObserver' in window)) return;

  bar.hidden = false;
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        bar.removeAttribute('data-visible');
      } else if (entry.boundingClientRect.top < 0) {
        bar.setAttribute('data-visible', '');
      } else {
        bar.removeAttribute('data-visible');
      }
    });
  }, { rootMargin: '0px 0px -40% 0px' });
  io.observe(buyBox);

  // keep the bar's price in step with the variant picker
  document.addEventListener('variant:change', function (e) {
    var target = bar.querySelector('[data-sticky-price]');
    var live = document.querySelector('.pdp__price [data-price]');
    if (target && live) target.textContent = live.textContent;
  });
})();
